/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-09-11
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Annonce de lancement de FaDel Delivery, partagee par les deux sites.
 *
 * Les visuels viennent des assets de la fiche Play Store du projet. Les exports
 * fournis avaient le damier de transparence aplati dans les pixels : il a ete
 * retire au detourage, et les fichiers publies sous `public/images/fadel/` sont
 * propres — inutile de les remplacer par les originaux.
 *
 * Les textes vivent ici plutot que dans `public/data/site.*.json` parce que les
 * deux sites n'ont ni les memes fichiers de traduction ni les memes langues :
 * le UK sert fr/en/es/ar, le Tchad fr/en/ar. Une source unique evite que
 * l'annonce diverge d'un site a l'autre au fil des retouches.
 */

const BASE = '/images/fadel';

/** Verrou de marque, sur son bleu nuit d'origine. */
export const FADEL_LOGO = `${BASE}/logo-fadel.webp`;

/**
 * Site public de FaDel. Le bouton d'appel pointe dessus plutot que vers la
 * fiche projet interne : le service existe deja, et l'annonce doit mener au
 * produit lui-meme, pas a une page qui le decrit.
 */
export const FADEL_URL = 'https://www.fadel-delivery.com';

/** Visuel promotionnel large, utilise en tete de section. */
export const FADEL_BANNER = `${BASE}/banner-1.webp`;

/**
 * Sept ecrans reels de l'application en service a N'Djamena (captures du
 * 3 octobre 2026), dans l'ordre d'une visite : accueil, repas, supermarche,
 * colis, taxi, pharmacie.
 */
export const FADEL_SCREENS = [
  {
    src: `${BASE}/screen-1.webp`,
    caption: {
      fr: 'Accueil : tous les services FaDel',
      en: 'Home: every FaDel service',
      es: 'Inicio: todos los servicios de FaDel',
      ar: 'الرئيسية: كل خدمات فاديل',
    },
  },
  {
    src: `${BASE}/screen-2.webp`,
    caption: {
      fr: 'Nourriture : les restaurants de N’Djamena',
      en: 'Food: restaurants across N’Djamena',
      es: 'Comida: los restaurantes de Yamena',
      ar: 'الطعام: مطاعم نجامينا',
    },
  },
  {
    src: `${BASE}/screen-3.webp`,
    caption: {
      fr: 'La carte d’un restaurant, prix en FCFA',
      en: 'A restaurant’s menu, prices in FCFA',
      es: 'La carta de un restaurante, precios en FCFA',
      ar: 'قائمة مطعم بالأسعار بالفرنك',
    },
  },
  {
    src: `${BASE}/screen-4.webp`,
    caption: {
      fr: 'Supermarché : rayons et promotions',
      en: 'Supermarket: aisles and offers',
      es: 'Supermercado: secciones y ofertas',
      ar: 'السوبرماركت: الأقسام والعروض',
    },
  },
  {
    src: `${BASE}/screen-5.webp`,
    caption: {
      fr: 'Colis : envoi en ville, même chez un tiers',
      en: 'Parcels: city delivery, even from a third party',
      es: 'Paquetes: envíos en la ciudad, incluso desde un tercero',
      ar: 'الطرود: توصيل داخل المدينة حتى من طرف ثالث',
    },
  },
  {
    src: `${BASE}/screen-6.webp`,
    caption: {
      fr: 'Taxi : départ, destination et paiement',
      en: 'Taxi: pick-up, destination and payment',
      es: 'Taxi: salida, destino y pago',
      ar: 'التاكسي: الانطلاق والوجهة والدفع',
    },
  },
  {
    src: `${BASE}/screen-7.webp`,
    caption: {
      fr: 'Pharmacie : ordonnances et pharmacies de garde',
      en: 'Pharmacy: prescriptions and on-duty pharmacies',
      es: 'Farmacia: recetas y farmacias de guardia',
      ar: 'الصيدلية: الوصفات والصيدليات المناوبة',
    },
  },
];

/** Pictogrammes des verticales couvertes par la plateforme. */
export const FADEL_VERTICALS = [
  {
    src: `${BASE}/service-nourriture.webp`,
    label: { fr: 'Restauration', en: 'Food', es: 'Restauración', ar: 'المطاعم' },
  },
  {
    src: `${BASE}/service-supermarche.webp`,
    label: { fr: 'Supermarché', en: 'Groceries', es: 'Supermercado', ar: 'البقالة' },
  },
  {
    src: `${BASE}/service-colis.webp`,
    label: { fr: 'Colis et plis', en: 'Parcels', es: 'Paquetería', ar: 'الطرود' },
  },
  {
    src: `${BASE}/service-taxi.webp`,
    label: { fr: 'Taxi', en: 'Ride-hailing', es: 'Taxi', ar: 'سيارات الأجرة' },
  },
  {
    src: `${BASE}/service-pharmacie.webp`,
    label: { fr: 'Pharmacie', en: 'Pharmacy', es: 'Farmacia', ar: 'الصيدلية' },
  },
  {
    src: `${BASE}/service-gaz.webp`,
    label: { fr: 'Gaz domestique', en: 'Cooking gas', es: 'Gas doméstico', ar: 'غاز الطهي' },
  },
  {
    src: `${BASE}/service-camion.webp`,
    label: { fr: 'Déménagement', en: 'Removals', es: 'Mudanzas', ar: 'النقل' },
  },
  {
    src: `${BASE}/service-cargo.webp`,
    label: { fr: 'Fret', en: 'Freight', es: 'Carga', ar: 'الشحن' },
  },
];

/** Photographies du catalogue FaDel Food. */
export const FADEL_DISHES = [
  { src: `${BASE}/plat-daraba.webp`, label: { fr: 'Daraba', en: 'Daraba', es: 'Daraba', ar: 'دارابا' } },
  { src: `${BASE}/plat-marara.webp`, label: { fr: 'Marara', en: 'Marara', es: 'Marara', ar: 'مرارة' } },
  {
    src: `${BASE}/plat-gombo-tchadien.webp`,
    label: { fr: 'Gombo tchadien', en: 'Chadian okra', es: 'Quingombó chadiano', ar: 'بامية تشادية' },
  },
  {
    src: `${BASE}/plat-poulet-dg.webp`,
    label: { fr: 'Poulet DG', en: 'Poulet DG', es: 'Pollo DG', ar: 'دجاج دي جي' },
  },
  {
    src: `${BASE}/plat-plantains-frits.webp`,
    label: { fr: 'Plantains frits', en: 'Fried plantain', es: 'Plátano frito', ar: 'موز مقلي' },
  },
  {
    src: `${BASE}/plat-jus-de-bissap.webp`,
    label: { fr: 'Jus de bissap', en: 'Bissap juice', es: 'Zumo de bissap', ar: 'عصير الكركديه' },
  },
];

/** Textes de l'annonce. */
export const FADEL_COPY = {
  badge: {
    fr: 'Bientôt en ligne',
    en: 'Launching soon',
    es: 'Próximamente',
    ar: 'قريبًا',
  },
  eyebrow: {
    fr: 'Lancement',
    en: 'Launch',
    es: 'Lanzamiento',
    ar: 'إطلاق',
  },
  title: {
    fr: 'FaDel Delivery arrive à N’Djamena',
    en: 'FaDel Delivery is coming to N’Djamena',
    es: 'FaDel Delivery llega a Yamena',
    ar: 'فاديل ديليفري قادم إلى نجامينا',
  },
  lead: {
    fr:
      'Repas, courses, colis et courses urbaines dans une seule application. Conçue et ' +
      'développée par Prenim, FaDel est une super-application de livraison pensée pour la ' +
      'zone CEMAC, avec N’Djamena comme ville pilote.',
    en:
      'Meals, groceries, parcels and city rides in a single app. Designed and built by ' +
      'Prenim, FaDel is a delivery super-app made for the CEMAC area, piloted in N’Djamena.',
    es:
      'Comidas, compras, paquetes y trayectos urbanos en una sola aplicación. Diseñada y ' +
      'desarrollada por Prenim, FaDel es una superaplicación de reparto pensada para la ' +
      'zona CEMAC, con Yamena como ciudad piloto.',
    ar:
      'وجبات ومشتريات وطرود وتنقلات داخل المدينة في تطبيق واحد. صممت برينيم وطورت فاديل، ' +
      'وهو تطبيق توصيل شامل مخصص لمنطقة CEMAC، مع نجامينا كمدينة رائدة.',
  },
  verticalsTitle: {
    fr: 'Huit services, une seule application',
    en: 'Eight services, one app',
    es: 'Ocho servicios, una sola aplicación',
    ar: 'ثماني خدمات في تطبيق واحد',
  },
  screensTitle: {
    fr: 'Un aperçu de l’application',
    en: 'A look at the app',
    es: 'Un vistazo a la aplicación',
    ar: 'لمحة عن التطبيق',
  },
  dishesTitle: {
    fr: 'Le catalogue FaDel Food',
    en: 'The FaDel Food catalogue',
    es: 'El catálogo FaDel Food',
    ar: 'كتالوج فاديل فود',
  },
  cta: {
    fr: 'Découvrir fadel-delivery.com',
    en: 'Visit fadel-delivery.com',
    es: 'Descubrir fadel-delivery.com',
    ar: 'زيارة fadel-delivery.com',
  },
};

/**
 * Texte d'une entree multilingue, avec repli sur le francais.
 *
 * Le site UK sert aussi l'espagnol, absent des traductions du Tchad : sans
 * repli, une langue non couverte afficherait un blanc au milieu de la section.
 */
export const fadelText = (entry, lang) =>
  (entry && (entry[lang] || entry.fr || entry.en)) || '';
